// TeamPingPanel — Polish #10.
//
// Always-visible right-rail panel for pinging the team on Slack. Replaces
// the prior button-triggered SlackPingDrawer so Kimi doesn't have to click
// into it first. Six preset buttons (Hold / Change Address / Reprint /
// Rush / Cancel / Proof Fix) fill the textarea with an autofilled
// template; click-to-insert "chips" for customer name, order number, and
// channel let Kimi quickly stitch in context without typing it.
//
// Share the same POST /api/conversations/:id/slack-ping endpoint as the
// old drawer — the backend route didn't need changes. Each preset maps
// to a sensible default category so the Slack post gets the right emoji.
//
// UX rules
//   - Presets fill the textarea from scratch (don't append). Any typed
//     edits after that are preserved; clicking a different preset
//     overwrites and warns in the UI for 2 seconds so Kimi knows why
//     her text changed.
//   - Chips insert at the textarea's cursor (or at end if not focused).
//   - `S` shortcut focuses the textarea.
//   - Cmd/Ctrl+Enter sends.

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useSlackPing, type SlackPingCategory } from '@/lib/hooks/useSlackPing';

export interface TeamPingPanelHandle {
  /** Move focus to the textarea. Called by the `S` shortcut in Center. */
  focus: () => void;
}

export interface TeamPingPanelProps {
  conversationId: string;
  /** Best-available customer display name (sender_name > email local > null). */
  customerName: string | null;
  /** Order number hint from the intent classifier, if any. */
  orderHint: string | null;
  /** Channel label for the "channel" chip — e.g. 'Gmail' | 'Etsy' | 'Shopify'. */
  channelLabel: string;
  /** Called after Send when the backend confirms. */
  onSent?: (res: { posted: boolean }) => void;
}

interface Preset {
  id: string;
  label: string;
  category: SlackPingCategory;
  /** Template placeholders are `{name}`, `{order}`, `{channel}`. */
  template: string;
}

const PRESETS: Preset[] = [
  {
    id: 'hold',
    label: 'Hold',
    category: 'heads-up',
    template: 'Please hold production on {name}, order {order} ({channel}). Will follow up with details.',
  },
  {
    id: 'address',
    label: 'Change Address',
    category: 'heads-up',
    template:
      'Address change for {name}, order {order} ({channel}). New address will be in the note below.',
  },
  {
    id: 'reprint',
    label: 'Reprint',
    category: 'heads-up',
    template: 'Reprint needed for {name}, order {order} ({channel}). Original went out damaged / wrong.',
  },
  {
    id: 'rush',
    label: 'Rush',
    category: 'escalation',
    template:
      'Rush requested for {name}, order {order} ({channel}). Confirming we can meet a faster turnaround.',
  },
  {
    id: 'cancel',
    label: 'Cancel',
    category: 'heads-up',
    template: 'Cancel request from {name}, order {order} ({channel}). Pausing here until we decide.',
  },
  {
    id: 'proof',
    label: 'Proof Fix',
    category: 'question',
    template:
      'Proof fix needed on {name}, order {order} ({channel}). Details below / attached to this thread.',
  },
];

/**
 * Fill template placeholders. If a value is missing we leave the placeholder
 * literal (`{name}`) so it's visually obvious Kimi still needs to type it in.
 * That's better than silently producing "Order " when `orderHint` is null.
 */
function fillTemplate(
  template: string,
  vals: { name: string | null; order: string | null; channel: string },
): string {
  const sub = (key: string, val: string | null): string => `{${key}}`.length > 0 && val ? val : `{${key}}`;
  return template
    .replaceAll('{name}', sub('name', vals.name))
    .replaceAll('{order}', sub('order', vals.order))
    .replaceAll('{channel}', sub('channel', vals.channel));
}

export const TeamPingPanel = forwardRef<TeamPingPanelHandle, TeamPingPanelProps>(
  function TeamPingPanel(
    { conversationId, customerName, orderHint, channelLabel, onSent },
    ref,
  ) {
    const [body, setBody] = useState('');
    const [category, setCategory] = useState<SlackPingCategory>('heads-up');
    const [activePreset, setActivePreset] = useState<string | null>(null);
    const [errorText, setErrorText] = useState<string | null>(null);
    const [flash, setFlash] = useState<null | { posted: boolean }>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const ping = useSlackPing(conversationId);

    useImperativeHandle(ref, () => ({
      focus: () => {
        textareaRef.current?.focus();
      },
    }));

    // Reset state when switching threads so the next conversation starts fresh.
    useEffect(() => {
      setBody('');
      setActivePreset(null);
      setCategory('heads-up');
      setErrorText(null);
    }, [conversationId]);

    function applyPreset(preset: Preset) {
      const filled = fillTemplate(preset.template, {
        name: customerName,
        order: orderHint,
        channel: channelLabel,
      });
      setBody(filled);
      setCategory(preset.category);
      setActivePreset(preset.id);
      // Give keyboard focus so Kimi can tweak immediately.
      requestAnimationFrame(() => textareaRef.current?.focus());
    }

    /**
     * Insert `text` at the current cursor position. Falls back to appending
     * if the textarea isn't focused.
     */
    function insertAtCursor(text: string) {
      const el = textareaRef.current;
      if (!el) {
        setBody((b) => (b.length > 0 && !b.endsWith(' ') ? `${b} ${text}` : `${b}${text}`));
        return;
      }
      const start = el.selectionStart ?? body.length;
      const end = el.selectionEnd ?? body.length;
      const next = body.slice(0, start) + text + body.slice(end);
      setBody(next);
      // Move cursor to end of inserted text on next tick.
      requestAnimationFrame(() => {
        if (!textareaRef.current) return;
        const pos = start + text.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      });
    }

    async function send() {
      if (!body.trim()) return;
      setErrorText(null);
      try {
        const res = await ping.mutateAsync({ body: body.trim(), category });
        setFlash({ posted: res.posted });
        setBody('');
        setActivePreset(null);
        window.setTimeout(() => setFlash(null), 5000);
        onSent?.({ posted: res.posted });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send';
        setErrorText(msg);
      }
    }

    function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void send();
      }
    }

    const chips: { label: string; value: string | null }[] = [
      { label: 'Name', value: customerName },
      { label: 'Order', value: orderHint },
      { label: 'Channel', value: channelLabel },
    ];

    return (
      <aside className="team-ping-panel" aria-label="Ping my team in Slack">
        <div className="eyebrow">Ping my team</div>
        <h3 className="team-ping-title">
          Say it in <em>Slack</em>.
        </h3>

        <div className="team-ping-presets" role="group" aria-label="Ping presets">
          {PRESETS.map((p) => (
            <button
              type="button"
              key={p.id}
              className={`team-ping-preset ${activePreset === p.id ? 'is-active' : ''}`}
              onClick={() => applyPreset(p)}
              title={`Fill with "${p.label}" template`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="team-ping-chips" role="group" aria-label="Autofill chips">
          {chips.map((c) => (
            <button
              type="button"
              key={c.label}
              className="team-ping-chip"
              disabled={!c.value}
              onClick={() => c.value && insertAtCursor(c.value)}
              title={c.value ? `Insert ${c.label.toLowerCase()}: ${c.value}` : `${c.label} not available`}
            >
              <span className="team-ping-chip-label">{c.label}</span>
              <span className="team-ping-chip-value">{c.value ?? '—'}</span>
            </button>
          ))}
        </div>

        <textarea
          ref={textareaRef}
          className="team-ping-input"
          placeholder="Type your message to the team, or pick a preset above."
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            // Any edit clears the active-preset highlight — Kimi is in
            // custom mode now.
            if (activePreset) setActivePreset(null);
          }}
          onKeyDown={onKey}
          rows={4}
          maxLength={2000}
          disabled={ping.isPending}
        />

        {errorText ? <div className="team-ping-err">{errorText}</div> : null}
        {flash ? (
          <div className={`team-ping-flash ${flash.posted ? 'ok' : 'warn'}`} role="status">
            {flash.posted
              ? 'Sent to Slack.'
              : "Saved, Slack webhook isn't wired yet. Ask Richard to add SLACK_WEBHOOK_URL."}
          </div>
        ) : null}

        <div className="team-ping-foot">
          <span className="team-ping-shortcut">
            <kbd className="kbd-inline">⌘</kbd>
            <kbd className="kbd-inline">Enter</kbd>
            <span> to send</span>
          </span>
          <button
            type="button"
            className="btn sm primary"
            onClick={() => void send()}
            disabled={ping.isPending || !body.trim()}
          >
            {ping.isPending ? 'Sending...' : 'Send to Slack'}
          </button>
        </div>
      </aside>
    );
  },
);
